/*
 * Remote-critter state. Lives next to the local player + AI NPCs in game.ts;
 * the loop calls `tickRemote(c, now)` each frame to advance interpolation.
 *
 * Strategy: when a `server_critter_moved` arrives, snapshot the critter's
 * current position as the "from" of a new interp segment and target the new
 * position as "to" with a fixed expected duration. Each tick we lerp between
 * them. If we hold an unfinished segment past a sane upper bound (the player
 * fell behind or there was a network gap), we snap to the target so the
 * critter doesn't appear frozen.
 *
 * Why a fixed expected duration vs. dynamic-from-the-actual-gap:
 *   The fixed duration (matches POSITION_UPDATE_HZ) keeps motion smooth even
 *   when packets are slightly jittered. The snap threshold handles the actual
 *   "gap is way too big" case without us having to track timing precisely.
 */

import type { NpcCritter, RemoteCritter } from '@/state/types';

/** Expected gap between server position updates (matches POSITION_UPDATE_HZ = 10). */
const INTERP_DURATION_MS = 120;

/** If a segment goes this long without an update, snap to target. */
const SNAP_THRESHOLD_MS = 500;

/**
 * Apply a new server-stamped position to a remote critter. Sets up the
 * interpolation segment; the loop's `tickRemote` will advance it each frame.
 */
export function applyRemotePosition(c: NpcCritter, x: number, y: number): void {
  if (c.kind !== 'remote') return;
  c.interp = {
    fromX: c.x,
    fromY: c.y,
    toX: x,
    toY: y,
    startedAt: performance.now(),
    durationMs: INTERP_DURATION_MS,
  };
  // Mirror the latest target into tx/ty so the scene's y-sort settles to the
  // server-authoritative position eventually even if the player tabs out and
  // back (snap path).
  c.tx = x;
  c.ty = y;
}

/** Advance a remote critter's position toward its current interp target. */
export function tickRemote(c: NpcCritter, now: number): void {
  if (c.kind !== 'remote' || !c.interp) return;
  const seg = c.interp;
  const elapsed = now - seg.startedAt;
  if (elapsed >= SNAP_THRESHOLD_MS || seg.durationMs <= 0) {
    c.x = seg.toX;
    c.y = seg.toY;
    return;
  }
  const t = Math.min(1, elapsed / seg.durationMs);
  c.x = seg.fromX + (seg.toX - seg.fromX) * t;
  c.y = seg.fromY + (seg.toY - seg.fromY) * t;
}

/**
 * Type alias: critters with `kind: 'remote'` for use in narrowing. We keep
 * this here rather than in types.ts because remote is a runtime-state notion
 * (interp + server-driven) more than a pure data shape.
 */
export type { RemoteCritter };
